import { cnpj } from 'cpf-cnpj-validator';
import { makeApi, makeFileApi } from 'src/http/api/internal/internal-http';
import { FileRepository } from 'src/repositories/files/file.repository';
import { PartnersRepository } from 'src/repositories/partners/partners.repository';
import { Partner } from 'src/types/partnes';
import { create } from 'zustand';

type States = {
  loadingPage: boolean;
  partners: Omit<Partner, 'image'>[];
  partnerToEdit: Partner | null;
  loadingDetails: boolean;
};

type Actions = {
  setLoadingPage: (loading: boolean) => void;
  fetchPartners: () => Promise<void>;
  getPartnerById: (id: string, callback: any) => Promise<void>;
  create: (partner: Partner) => Promise<boolean>;
  getUrlFile: (file: string) => Promise<string>;
  getLegacyUrlFile: (file: string, fileName?: string | null) => Promise<string>;
  update: (partner: Partner) => Promise<boolean>;
  reset: () => void;
  findPartners: (onlyNoGiftback?: boolean) => Promise<Partner[] | null>;
  findCompanies: () => Promise<Partner[] | null>;
};

type PartnersStore = States & Actions;

const initialState = {
  loadingPage: false,
  partners: [],
  partnerToEdit: null,
  loadingDetails: false,
};

const repository = new PartnersRepository(makeApi());
const fileRepository = new FileRepository(makeFileApi());

export const usePartnersStore = create<PartnersStore>((set) => ({
  ...initialState,
  setLoadingPage: (loading) => set({ loadingPage: loading }),
  fetchPartners: async () => {
    set({ loadingPage: true });
    const response = await repository.getPartners();
    if (response.isLeft()) {
      set({ partners: [], loadingPage: false });
      return;
    }
    const { value } = response;
    const partners = value.map((v: any) => ({
      _id: v._id,
      name: v.name,
      cnpj: cnpj.format(v.cnpj),
      status: v.status,
    }));
    set({ partners: partners as Partner[], loadingPage: false });
  },

  getPartnerById: async (id: string, callback: any) => {
    set({ loadingPage: true });
    const response = await repository.getPartnerById(id);
    if (response.isLeft()) {
      set({ partnerToEdit: null, loadingPage: false });
      return;
    }
    const { value } = response;
    callback('name', value.name);
    callback('cnpj', value.cnpj);
    callback('details', value.details);
    callback('status', value.status);
    callback('image', value.image);
    set({ loadingPage: false });
  },

  create: async (partner) => {
    set({ loadingDetails: true });
    const response = await repository.create(partner);
    if (response.isLeft()) {
      set({ loadingDetails: false });
      return false;
    }
    set({ loadingDetails: false });
    return true;
  },

  getUrlFile: async (file) => {
    const response = await fileRepository.upload(file);
    if (response.isLeft()) {
      return 'error';
    }
    return response.value.data.url;
  },

  getLegacyUrlFile: async (file, fileName) => {
    const response = await fileRepository.uploadLegacy(file, fileName);
    if (response.isLeft()) {
      return 'error';
    }
    return response.value.data.url;
  },

  update: async (partner) => {
    set({ loadingDetails: true });
    const response = await repository.update(partner);
    if (response.isLeft()) {
      set({ loadingDetails: false });
      return false;
    }
    set({ loadingDetails: false });
    return true;
  },
  reset: () => set({ partnerToEdit: null }),

  findPartners: async (onlyNoGiftback = false) => {
    const response = await repository.getPartners();
    if (response.isLeft()) {
      return null;
    }

    return (
      response.value?.filter(
        (p: any) =>
          p.status === 'active' && (!onlyNoGiftback || p._id !== '6786647b3fc4f20f87ac5901')
      ) || []
    );
  },

  findCompanies: async () => {
    const response = await repository.getCompanies();
    if (response.isLeft()) {
      return null;
    }

    return (
      response.value
    );
  },
}));
